import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { ROUTES } from "../config/routes";
import { appSettings } from "../config/appSettings";
import { CompanyViewModel } from "../api/generated/models/CompanyViewModel";
import { PartnerAccountViewModel } from "../api/generated/models/PartnerAccountViewModel";
import { AcceptedTermsOfServiceVersionViewModel } from "../api/generated/models/AcceptedTermsOfServiceVersionViewModel";
import { BusinessAccountService } from "../api/generated/services/BusinessAccountService";
import { PartnerAccountsService } from "../api/generated/services/PartnerAccountsService";
import InvitePartnerAccountModal from "../components/PartnerManagement/modals/InvitePartnerAccountModal";
import PartnerAccountActionsPopover from "../components/PartnerManagement/modals/PartnerAccountActionsPopover";
import ResendPartnerInvitationModal from "../components/PartnerManagement/modals/ResendPartnerInvitationModal";
import RemovePartnerAccessModal from "../components/PartnerManagement/modals/RemovePartnerAccessModal";
import SendManuallyPartnerInvitationModal from "../components/PartnerManagement/modals/SendManuallyPartnerInvitationModal";
import { ReactComponent as ArrowRight } from "../assets/icons/arrow-right.svg";
import { ReactComponent as RefreshIcon } from "../assets/icons/refresh.svg";
import "../api/openApiConfig";

const PartnerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<CompanyViewModel | null>(null);
  const [partnerAccounts, setPartnerAccounts] = useState<PartnerAccountViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [loginAsPartnerLoading, setLoginAsPartnerLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [selectedPartnerAccount, setSelectedPartnerAccount] =
    useState<PartnerAccountViewModel | null>(null);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isSendManuallyModalOpen, setIsSendManuallyModalOpen] = useState(false);
  const [acceptedTosVersions, setAcceptedTosVersions] = useState<AcceptedTermsOfServiceVersionViewModel[]>([]);
  const [latestTosVersion, setLatestTosVersion] = useState<number | null>(null);
  const [tosLoading, setTosLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);

      // Fetch company and partner accounts in parallel
      const [companyResponse, accountsResponse] = await Promise.all([
        BusinessAccountService.postApiBusinessAccountGetCompanyForAdmin({ companyId: id }),
        PartnerAccountsService.postApiPartnerAccountsListPartnerAccounts({ companyId: id }),
      ]);

      setCompany(companyResponse);
      setPartnerAccounts(accountsResponse);
      setError(null);
    } catch (err: any) {
      console.error("Error fetching partner details:", err);
      if (err?.status === 404) {
        setError("Partner not found.");
      } else {
        setError("Failed to load partner details. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch TOS data (non-blocking)
  const fetchTosData = useCallback(async () => {
    if (!id) return;

    try {
      setTosLoading(true);
      const [acceptedResponse, latestResponse] = await Promise.all([
        BusinessAccountService.postApiBusinessAccountGetAcceptedTermsOfServiceByCompany({ companyId: id }),
        BusinessAccountService.postApiBusinessAccountGetLatestTermsOfService({}),
      ]);

      setAcceptedTosVersions(acceptedResponse);
      if (latestResponse.version !== undefined) {
        setLatestTosVersion(latestResponse.version);
      }
    } catch (err) {
      console.error("Error fetching TOS data:", err);
    } finally {
      setTosLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchData();
      fetchTosData();
    }
  }, [id, fetchData, fetchTosData]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleBack = () => {
    navigate(ROUTES.ADMIN_PARTNER_MANAGEMENT());
  };

  const handleLoginAsPartner = async () => {
    if (!id) return;
    try {
      setLoginAsPartnerLoading(true);
      await BusinessAccountService.postApiBusinessAccountLoginOnBehalfOfCompany({
        companyId: id,
      });
      // Full page refresh to root on success
      window.location.href = "/";
    } catch (err) {
      console.error("Error logging in as partner:", err);
      setError("Failed to login as partner. Please try again.");
      setLoginAsPartnerLoading(false);
    }
  };

  const handleInviteSuccess = async () => {
    setSuccessMessage("Invitation sent successfully!");
    // Refresh the partner accounts list
    if (id) {
      try {
        const accountsResponse =
          await PartnerAccountsService.postApiPartnerAccountsListPartnerAccounts({
            companyId: id,
          });
        setPartnerAccounts(accountsResponse);
      } catch (err) {
        console.error("Error refreshing partner accounts:", err);
      }
    }
  };

  const refreshPartnerAccounts = async () => {
    if (id) {
      try {
        const accountsResponse =
          await PartnerAccountsService.postApiPartnerAccountsListPartnerAccounts({
            companyId: id,
          });
        setPartnerAccounts(accountsResponse);
      } catch (err) {
        console.error("Error refreshing partner accounts:", err);
      }
    }
  };

  const handleResendInvitation = (account: PartnerAccountViewModel) => {
    setSelectedPartnerAccount(account);
    setIsResendModalOpen(true);
  };

  const handleRemoveAccess = (account: PartnerAccountViewModel) => {
    setSelectedPartnerAccount(account);
    setIsRemoveModalOpen(true);
  };

  const handleSendManually = (account: PartnerAccountViewModel) => {
    setSelectedPartnerAccount(account);
    setIsSendManuallyModalOpen(true);
  };

  const handleResendSuccess = () => {
    setSuccessMessage("Invitation resent successfully!");
    refreshPartnerAccounts();
  };

  const handleRemoveSuccess = () => {
    setSuccessMessage("Access removed successfully!");
    refreshPartnerAccounts();
  };

  if (loading) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  if (error && !company) {
    return (
      <div>
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-2 gap-2 text-gray-600 justify-start pl-0"
        >
          <ArrowRight className="w-4 h-4 rotate-180" />
          Back to Partner Management
        </Button>
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div>
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-2 gap-2 text-gray-600 justify-start pl-0"
        >
          <ArrowRight className="w-4 h-4 rotate-180" />
          Back to Partner Management
        </Button>
        <div className="text-center text-gray-500 my-8">
          Partner not found
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={handleBack}
        className="mb-2 gap-2 text-gray-600 justify-start pl-0"
      >
        <ArrowRight className="w-4 h-4 rotate-180" />
        Back to Partner Management
      </Button>

      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <h2 className="font-medium">{company.name || "Partner Details"}</h2>
        <Button
          onClick={() => {
            fetchData();
            fetchTosData();
          }}
          variant="outline"
          size="sm"
          className="gap-1"
          disabled={loading}
        >
          <RefreshIcon className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
        <Button
          onClick={handleLoginAsPartner}
          size="sm"
          disabled={loginAsPartnerLoading}
        >
          {loginAsPartnerLoading ? "Logging in..." : "Login as Partner"}
        </Button>
      </div>

      {/* Error Notification */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            x
          </button>
        </div>
      )}

      {/* Success Notification */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-4 flex justify-between items-center">
          <span>{successMessage}</span>
          <button
            onClick={() => setSuccessMessage(null)}
            className="text-green-600 hover:text-green-800"
          >
            x
          </button>
        </div>
      )}

      {/* Company Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4 text-gray-800">
            Company Information
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Company Name</span>
              <span className="font-medium">{company.name || "-"}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Email</span>
              <span className="font-medium">{company.email || "-"}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">CVR Number</span>
              <span className="font-medium">{company.cvrNumber || "-"}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Billing ID</span>
              <div className="flex flex-col items-end">
                <span className="font-medium">{company.billwerkCustomerId || "-"}</span>
                {company.billwerkCustomerId && (
                  <a
                    href={`${appSettings.BILLWERK_CUSTOMER_BASE_URL}/${company.billwerkCustomerId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline mt-1"
                  >
                    See in Billwerk
                  </a>
                )}
              </div>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Pending Changes</span>
              <span>
                {company.hasPendingChanges ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    Yes
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    No
                  </span>
                )}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Company ID</span>
              <div className="flex flex-col items-end">
                <span className="font-medium">{company.id || "-"}</span>
                {company.id && (
                  <a
                    href={`${process.env.REACT_APP_CONTENTFUL_BASE_URL}/entries/${company.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline mt-1"
                  >
                    See in Contentful
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Terms of Service Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4 text-gray-800">
            Terms of Service{latestTosVersion !== null ? ` (latest = v${latestTosVersion})` : ""}
          </h3>
          {tosLoading ? (
            <div className="flex justify-center py-4">
              <svg className="w-5 h-5 animate-spin text-gray-400" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          ) : acceptedTosVersions.length === 0 ? (
            <div className="text-gray-500 text-sm">-</div>
          ) : (
            <div className="space-y-2">
              {(() => {
                const hasAcceptedLatest = latestTosVersion !== null && acceptedTosVersions.some(tos => tos.version === latestTosVersion);
                const formatAcceptedAt = (dateString?: string | null) => {
                  if (!dateString) return null;
                  return new Date(dateString).toLocaleString("da-DK", {
                    timeZone: "Europe/Copenhagen",
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  });
                };
                return acceptedTosVersions.map((tos) => (
                  <div
                    key={tos.version}
                    className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex flex-col">
                      <span className="text-gray-600 text-sm">
                        {tos.title || `Version ${tos.version}`}
                      </span>
                      {tos.acceptedAt && (
                        <span className="text-gray-400 text-xs">
                          Accepted: {formatAcceptedAt(tos.acceptedAt)}
                        </span>
                      )}
                    </div>
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        hasAcceptedLatest
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      v{tos.version}
                    </span>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Dashboard Access Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <h2 className="font-medium">Dashboard Access</h2>
            <Button
              onClick={() => refreshPartnerAccounts()}
              variant="outline"
              size="sm"
              className="gap-1"
            >
              <RefreshIcon className="w-3 h-3" />
              Refresh
            </Button>
          </div>
          <Button size="sm" onClick={() => setIsInviteModalOpen(true)}>
            Invite
          </Button>
        </div>
        {partnerAccounts.length === 0 ? (
          <div className="text-center text-gray-500 my-8 bg-white rounded-lg shadow p-6">
            No dashboard access accounts found
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="table-default">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Invitation Status</th>
                  <th className="w-16">Actions</th>
                </tr>
              </thead>
              <tbody>
                {partnerAccounts.map((account) => (
                  <tr key={account.id}>
                    <td className="font-medium">{account.email || "-"}</td>
                    <td>
                      {account.acceptedInvitation ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <svg
                            className="w-3.5 h-3.5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Accepted
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          <svg
                            className="w-3.5 h-3.5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Pending
                        </span>
                      )}
                    </td>
                    <td>
                      <PartnerAccountActionsPopover
                        onResendInvitation={() => handleResendInvitation(account)}
                        onRemoveAccess={() => handleRemoveAccess(account)}
                        isPending={!account.acceptedInvitation}
                        onSendManually={() => handleSendManually(account)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite Partner Account Modal */}
      {id && (
        <InvitePartnerAccountModal
          isOpen={isInviteModalOpen}
          companyId={id}
          onClose={() => setIsInviteModalOpen(false)}
          onSuccess={handleInviteSuccess}
        />
      )}

      {/* Resend Partner Invitation Modal */}
      <ResendPartnerInvitationModal
        isOpen={isResendModalOpen}
        partnerAccount={selectedPartnerAccount}
        onClose={() => {
          setIsResendModalOpen(false);
          setSelectedPartnerAccount(null);
        }}
        onSuccess={handleResendSuccess}
      />

      {/* Send Manually Partner Invitation Modal */}
      <SendManuallyPartnerInvitationModal
        isOpen={isSendManuallyModalOpen}
        partnerAccount={selectedPartnerAccount}
        onClose={() => {
          setIsSendManuallyModalOpen(false);
          setSelectedPartnerAccount(null);
        }}
      />

      {/* Remove Partner Access Modal */}
      <RemovePartnerAccessModal
        isOpen={isRemoveModalOpen}
        partnerAccount={selectedPartnerAccount}
        onClose={() => {
          setIsRemoveModalOpen(false);
          setSelectedPartnerAccount(null);
        }}
        onSuccess={handleRemoveSuccess}
      />
    </div>
  );
};

export default PartnerDetails;
